import React, { useState, useEffect, useRef } from 'react';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  OutlinedInput,
  InputLabel,
  Tab,
  Tabs,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Description as DocxFileIcon,
  Code as XmlFileIcon,
  PictureAsPdf as PdfFileIcon,
  KeyboardArrowDown as ArrowDownIcon,
  HelpOutline as OpenQuestionIcon,
} from '@mui/icons-material';

import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, TableOfContents, AlignmentType, BorderStyle, VerticalAlignTable, WidthType } from 'docx';
import { pdf } from '@react-pdf/renderer';
import { getPredefinedSapUsdmSections } from '../constants/sapSectionDataUsdm';
import ScrollToTop from '../../components/common/ScrollToTop';
import StyleComplianceCheck from '../../components/common/StyleComplianceCheck';
import { generateContent } from '../../services/AIService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import documentEditorService from '../../services/documentEditor.service';
import protocolRegistrationService from '../../services/protocolRegistration.service';
import administrativeInfoService from '../../services/administrativeInfo.service';
import { projectService } from '../../services/projectService';
import { extractUsdmFacts, formatUsdmFactsForPrompt } from '../utils/usdmToSapContext';
import { REGULATORY_FONT_CONFIG, ptToHalfPoints, fontConfigForHeadingLevel } from '../../utils/regulatoryFontConfig';
import { toSentenceCaseTable, parseTableCaptionLine, unboldCrossReferenceSentences } from '../../utils/tableTextFormat';
import { useProject } from '../../context/ProjectContext';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import { fetchLatestClinicalIntakeData } from '../../services/clinicalIntake.service';
import { colors as dizzarooColors } from '../../theme/tokens';
import SAPPdfDocument, { buildSapPdfTocEntries } from '../../components/documentEditor/SAPPdfDocument';
import EditableSectionContent from '../../protocol/components/EditableSectionContent';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import { getProjectIdentityFields } from '../../utils/projectIdentityFields';
import sapXmlExportService from '../services/sapXmlExport.service';
import { startSpan, endSpan } from '../../telemetry/telemetry';
//import clinicalIntakeService from '../../services/clinicalIntake.service';

// --- eSAP XML export ---------------------------------------------------
//
// Exports each section's rich-text output as XML conforming to
// src/sap/schema/esap-sap-1.0.xsd. CDISC has no official XSD for a SAP —
// USDM and ARS (the CDISC standards that cover SAP-adjacent content) are
// JSON/YAML, not XML (confirmed against cdisc-org/DDF-RA discussion #612).
// This vocabulary is an eSAP-internal schema modeled on the ICH M11 CeSP
// XML conventions already used elsewhere in this codebase — Section /
// Subsection carrying an `identifier` and `name`, with Text / List /
// Table(Row/Cell) content — so a SAP export reads the same way an M11
// protocol export does, rather than inventing an unrelated shape.

const ESAP_SAP_NAMESPACE = 'urn:esap:sap:1.0';
const ESAP_SAP_SCHEMA_LOCATION = 'esap-sap-1.0.xsd';

// Helper function to escape XML special characters
function escapeXml(text) {
  if (typeof text !== 'string') return text;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function runsToText(runs) {
  return (runs || []).map((r) => r.text || '').join('').replace(/\s+/g, ' ').trim();
}

// Turns the flat block list from richTextToBlocks() into a Section/Subsection
// tree. Each block's heading level (from its DOM tag, or from the numbering
// heuristic richTextToBlocks already applies) decides nesting: a heading
// closes every open node at its level or deeper, then opens as a child of
// whatever remains — the standard heading-to-tree algorithm.
function blocksToSectionTree(blocks, mainNum, sectionTitle) {
  const root = {
    identifier: mainNum || null, name: sectionTitle,
    texts: [], lists: [], tables: [], children: [],
  };

  // Drop a leading heading that just restates the section title (e.g.
  // "3.0 STATISTICAL FRAMEWORK" under a section already titled "Statistical
  // Framework") — the same rule the Word export applies via
  // isDuplicateTitleHeading, so both exports agree on what counts as content.
  const usable =
    blocks[0]?.type === 'heading' && isDuplicateTitleHeading(blocks[0].text, sectionTitle)
      ? blocks.slice(1)
      : blocks;

  const stack = [{ level: 0, node: root }];

  usable.forEach((b) => {
    if (b.type === 'heading') {
      // richTextToBlocks already classified this as a heading from its DOM
      // tag, so a direct number-prefix match is enough here — unlike
      // parseProtocolHeadingLine's isLikelyProtocolHeading, this doesn't need
      // to *guess* whether text is a heading (that heuristic exists for
      // scanning plain prose, and rejects some real headings, e.g. a
      // colon-labeled one with a long tail: "3.3.1 Primary Estimand:
      // Disease-Free Survival (DFS)").
      const numbered = b.text.match(/^(\d+(?:\.\d+)*)\.?\s+(.+)$/);
      const node = {
        identifier: numbered ? numbered[1] : null,
        name: numbered ? numbered[2].trim() : b.text,
        texts: [], lists: [], tables: [], children: [],
      };
      const level = b.level || 1;
      while (stack.length > 1 && stack[stack.length - 1].level >= level) stack.pop();
      stack[stack.length - 1].node.children.push(node);
      stack.push({ level, node });
      return;
    }
    const current = stack[stack.length - 1].node;
    if (b.type === 'paragraph') {
      const text = runsToText(b.runs);
      if (text) current.texts.push(text);
    } else if (b.type === 'list') {
      const items = (b.items || []).map(runsToText).filter(Boolean);
      if (items.length) current.lists.push({ ordered: !!b.ordered, items });
    } else if (b.type === 'table') {
      const rows = (b.rows || []).map((row) => (row || []).map(runsToText));
      if (rows.length) current.tables.push(rows);
    }
  });

  return root;
}

function serializeSectionNode(node, tagName, indent) {
  const identifierAttr = node.identifier ? ` identifier="${escapeXml(node.identifier)}"` : '';
  let xml = `${indent}<${tagName}${identifierAttr} name="${escapeXml(node.name || '')}">\n`;
  node.texts.forEach((text) => {
    xml += `${indent}  <Text>${escapeXml(text)}</Text>\n`;
  });
  node.lists.forEach((list) => {
    xml += `${indent}  <List ordered="${list.ordered}">\n`;
    list.items.forEach((item) => {
      xml += `${indent}    <Item>${escapeXml(item)}</Item>\n`;
    });
    xml += `${indent}  </List>\n`;
  });
  node.tables.forEach((rows) => {
    xml += `${indent}  <Table>\n`;
    rows.forEach((row) => {
      xml += `${indent}    <Row>\n`;
      row.forEach((cell) => {
        xml += `${indent}      <Cell>${escapeXml(cell)}</Cell>\n`;
      });
      xml += `${indent}    </Row>\n`;
    });
    xml += `${indent}  </Table>\n`;
  });
  node.children.forEach((child) => {
    xml += serializeSectionNode(child, 'Subsection', indent + '  ');
  });
  xml += `${indent}</${tagName}>\n`;
  return xml;
}

// Dizzaroo brand theme – Primary: Deep Blue #1E73BE (fallbacks so CircularProgress never sees undefined palette.primary)
const theme = createTheme({
  palette: {
    primary: {
      main: dizzarooColors?.primary?.[500] ?? '#1E73BE',
      light: dizzarooColors?.primary?.light ?? '#4A8FD4',
      dark: dizzarooColors?.primary?.[600] ?? '#1E6091',
      contrastText: '#ffffff',
    },
    secondary: {
      main: dizzarooColors?.accent?.teal ?? '#168AAD',
      light: dizzarooColors?.accent?.tealLight ?? '#168AAD',
      dark: dizzarooColors?.accent?.tealDark ?? '#168AAD',
      contrastText: '#ffffff',
    },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    subtitle2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    body1: { fontFamily: '"Urbanist", sans-serif' },
    body2: { fontFamily: '"Urbanist", sans-serif' },
    button: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&display=swap');
      `,
    },
    MuiButton: {
      defaultProps: { color: 'primary', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
        contained: {
          // :not(.Mui-disabled) matters: without it, this forces the solid
          // blue background on a *disabled* button too, and MUI's default
          // disabled text color (a washed-out grey) becomes nearly invisible
          // against it — that's the "text not visible" bug (e.g. "Generate
          // All Sections" once every section already has content). Leaving
          // disabled buttons alone lets MUI's own (already-legible) disabled
          // styling apply instead.
          '&.MuiButton-colorPrimary:not(.Mui-disabled)': {
            backgroundColor: dizzarooColors?.primary?.[500] ?? '#1E73BE',
            '&:hover': { backgroundColor: dizzarooColors?.primary?.[600] ?? '#1E6091' },
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } } },
  },
});

const SAP_TITLE_PAGE_FIELD_ROWS = [
  ['confidentialityStatement', 'Sponsor Confidentiality Statement:'],
  ['documentType', 'Document Type:'],
  ['versionNumber', 'Version Number:'],
  ['fullTitle', 'Full Title:'],
  ['trialPhase', 'Trial Phase:'],
  ['trialAcronym', 'Trial Acronym:'],
  ['shortTitle', 'Short Title:'],
  ['sponsorProtocolIdentifier', 'Sponsor Protocol Identifier:'],
  ['sponsorProductCodes', "Sponsor's Investigational Product Code(s):"],
  ['investigationalProductNames', 'Investigational Product Name(s):'],
  ['nctNumber', 'NCT No.:'],
  ['eudraCtNumber', 'EudraCT No.:'],
  ['sponsorNameAndAddress', 'Sponsor Name and Address'],
];

const SAP_DEFAULT_APPROVERS = [
  { role: 'Lead Statistician', printedName: '' },
  { role: 'Clinical Science Lead', printedName: '' },
  { role: 'Statistical Programming Lead', printedName: '' },
];

/**
 * Title Page — a dedicated front-matter page rendered from the structured
 * SapTitlePage record (see sapTitlePage.model.js), not a generated SAP
 * section, matching the two-column label/value table in the SAP title-page
 * template (Title Page Format_SAP.docx: Sponsor Confidentiality Statement,
 * Document Type, Version Number, Full Title, ... Sponsor Name and Address).
 */
function buildSapTitlePageDocxChildren(titlePage, fontConfig = REGULATORY_FONT_CONFIG) {
  const tp = titlePage || {};
  const children = [];

  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: SAP_DOCX_PT(18) },
      children: [
        new TextRun({
          text: 'Title Page',
          font: fontConfig.title.name,
          size: ptToHalfPoints(fontConfig.title.size),
          bold: true,
          color: fontConfig.title.color,
        }),
      ],
    })
  );

  const cell = (text, bold) =>
    new TableCell({
      margins: sapTableCellMarginsDocx(),
      verticalAlign: VerticalAlignTable.CENTER,
      borders: sapTableCellBordersDocx(),
      children: [
        new Paragraph({
          alignment: AlignmentType.LEFT,
          spacing: { line: 240, before: 0, after: 0 },
          children: [
            new TextRun({
              text: text || '',
              font: fontConfig.body.name,
              size: ptToHalfPoints(fontConfig.body.size),
              bold: !!bold,
              color: fontConfig.body.color,
            }),
          ],
        }),
      ],
    });

  const tableRows = SAP_TITLE_PAGE_FIELD_ROWS.map(
    ([field, label]) =>
      new TableRow({
        children: [cell(label, true), cell(tp[field], false)],
      })
  );

  children.push(new Table({ rows: tableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
  children.push(new Paragraph({ children: [], pageBreakBefore: true }));
  return children;
}

/**
 * Approvers and Signatories — dedicated front-matter page (Role / Printed
 * Name / Signature / Date), printed name from the SapTitlePage record;
 * Signature/Date are left blank here for the real e-signature workflow.
 */
function buildSapApproversDocxChildren(titlePage, fontConfig = REGULATORY_FONT_CONFIG) {
  const children = [];

  children.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: SAP_DOCX_PT(12) },
      children: [
        new TextRun({
          text: 'Approvers and Signatories',
          font: fontConfig.heading.name,
          size: ptToHalfPoints(fontConfig.heading.size),
          bold: true,
          color: fontConfig.heading.color,
        }),
      ],
    })
  );

  const approvers =
    titlePage?.approvers && titlePage.approvers.length ? titlePage.approvers : SAP_DEFAULT_APPROVERS;

  const headerCell = (text) =>
    new TableCell({
      margins: sapTableCellMarginsDocx(),
      verticalAlign: VerticalAlignTable.CENTER,
      borders: sapTableCellBordersDocx(),
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text,
              font: fontConfig.tableHeader.name,
              size: ptToHalfPoints(fontConfig.tableHeader.size),
              bold: true,
              color: fontConfig.tableHeader.color,
            }),
          ],
        }),
      ],
    });
  const bodyCell = (text) =>
    new TableCell({
      margins: sapTableCellMarginsDocx(),
      verticalAlign: VerticalAlignTable.CENTER,
      borders: sapTableCellBordersDocx(),
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: text || '',
              font: fontConfig.tableCell.name,
              size: ptToHalfPoints(fontConfig.tableCell.size),
              color: fontConfig.tableCell.color,
            }),
          ],
        }),
      ],
    });

  const tableRows = [
    new TableRow({
      tableHeader: true,
      children: [headerCell('Role'), headerCell('Printed Name'), headerCell('Signature'), headerCell('Date')],
    }),
    ...approvers.map(
      (a) =>
        new TableRow({
          children: [bodyCell(a.role), bodyCell(a.printedName), bodyCell(''), bodyCell('')],
        })
    ),
  ];

  children.push(new Table({ rows: tableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
  children.push(new Paragraph({ children: [], pageBreakBefore: true }));
  return children;
}

const generateSapTableOfContents = () => {
  // Use a Word-native TOC field so Word can calculate page numbers dynamically.
  // Headings included will be all paragraphs with an outlineLevel (0-3) that we already
  // set per section/subsection; the index text below is just a visual guide.
  const tocParagraphs = [];

  // TOC title — styled to look like a heading (font/size/bold below) but deliberately given
  // no outlineLevel: the field below picks up every outlineLevel'd paragraph, and an
  // outlineLevel'd title here would list itself as its own first TOC entry (same fix as the
  // protocol DOCX export's TOC title, see generateWord2024TableOfContents in DocumentEditor.jsx).
  const tocTitleFont = REGULATORY_FONT_CONFIG.heading;
  tocParagraphs.push(
    new Paragraph({
      style: 'CFrontMatter',
      alignment: AlignmentType.LEFT,
      spacing: { after: SAP_DOCX_PT(12), before: 0 },
      children: [
        new TextRun({
          text: 'Table of Contents',
          font: tocTitleFont.name,
          size: ptToHalfPoints(tocTitleFont.size),
          bold: tocTitleFont.bold,
          color: tocTitleFont.color,
        }),
      ],
    })
  );

  // Native TOC field: Word will populate headings + page numbers on update
  tocParagraphs.push(
    new TableOfContents('', {
      hyperlink: true,
      headingStyleRange: '1-6',
      // Headings use custom "C-Heading N" styles (not built-in Heading1-6). The \o switch
      // (headingStyleRange) alone only picks up paragraphs on the BUILT-IN Heading 1-9
      // styles - useAppliedParagraphOutlineLevel adds the \u switch, which makes Word also
      // include any paragraph with a directly-set outline level, regardless of style name.
      // (useOutlineLevels/useHeadingStyles used here before are not real docx properties -
      // TableOfContents passes its properties straight through to the raw TOC field code
      // with no name translation, so unrecognized keys are silently dropped.)
      useAppliedParagraphOutlineLevel: true,
    })
  );

  return tocParagraphs;
};

// Strips a leading numbering prefix (e.g. "3.", "3.0", "13.2") so heading
// text can be compared ignoring numbering-format differences.
function stripLeadingNumber(text) {
  return String(text || '').replace(/^\s*\d+(\.\d+)*\.?\s*/, '').trim();
}

function normalizeHeadingText(text) {
  return stripLeadingNumber(text).toLowerCase().replace(/\s+/g, ' ').trim();
}

// True when a generated section's first heading just restates the section
// title (e.g. content beginning "3.0 STATISTICAL FRAMEWORK" under a section
// already titled "3. Statistical Framework" in the export). Used to drop
// that redundant heading so the title isn't printed twice in exports.
function isDuplicateTitleHeading(headingText, sectionTitle) {
  const a = normalizeHeadingText(headingText);
  const b = normalizeHeadingText(sectionTitle);
  return Boolean(a) && a === b;
}

// Scans a section's generated HTML for "Open Question(s)" markers (the SAP
// section prompts explicitly instruct the model to add one instead of
// guessing a value it doesn't have) and pulls out the actual question text.
// Best-effort/heuristic, since the model's exact formatting isn't fixed:
// looks block-by-block (heading/paragraph/list-item/table-cell) for text
// starting with "Open Question(s):"; if the label has no text after it on
// the same block, the very next block is taken as the question.
function extractOpenQuestions(html) {
  if (!html) return [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const blockEls = Array.from(tempDiv.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li,td'));
  const lines = (blockEls.length ? blockEls : [tempDiv])
    .map((el) => el.textContent.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const labelRe = /open\s+questions?\s*:?/i;
  const questions = [];
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(labelRe);
    if (!match) continue;
    const idx = lines[i].search(labelRe);
    const sameLineRemainder = lines[i].slice(idx + match[0].length).trim();
    if (sameLineRemainder) {
      questions.push(sameLineRemainder);
    } else if (lines[i + 1] && !labelRe.test(lines[i + 1])) {
      questions.push(lines[i + 1]);
    }
  }
  return Array.from(new Set(questions));
}

/** True when a run list is a single plain-text run, safe to sentence-case as a whole. */
function cellRunsToDisplayRuns(cellRuns, sentenceCase) {
  const runs = cellRuns || [];
  if (sentenceCase && runs.length === 1 && !runs[0].bold && !runs[0].italic) {
    return [{ ...runs[0], text: toSentenceCaseTable(runs[0].text || '') }];
  }
  return runs;
}

// points -> twips (docx.js spacing/indent units), matching the protocol DOCX export's DRAFT_PT/DRAFT_CM_TO_DXA.
const SAP_DOCX_PT = (pt) => Math.round(pt * 20);
const SAP_DOCX_CM_TO_DXA = (cm) => Math.round((Number(cm) / 2.54) * 1440);
const SAP_TABLE_CELL_MARGIN_LR_TWIPS = Math.max(1, Math.round(0.1 * 20));

const sapTableCellMarginsDocx = () => ({
  top: 0,
  bottom: 0,
  left: SAP_TABLE_CELL_MARGIN_LR_TWIPS,
  right: SAP_TABLE_CELL_MARGIN_LR_TWIPS,
});

const sapTableCellBordersDocx = () => ({
  top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
  bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
  left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
  right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
});

/**
 * Named paragraph styles for the SAP DOCX export — same C-* naming/roles as the protocol
 * export's styles block (generateWord2024TableOfContents/exportToDocx in DocumentEditor.jsx),
 * built from REGULATORY_FONT_CONFIG so Word shows real style names (C-Heading 1, C-Body Text,
 * ...) instead of everything being direct/inline formatting on top of bare "Normal".
 */
function buildSapDocxStyles(fontConfig) {
  const lineSpacing = (role) => Math.round((fontConfig[role]?.lineSpacing || 1.15) * 240);
  return {
    paragraphStyles: [
      {
        id: 'Normal', name: 'Normal',
        run: { font: fontConfig.body.name, size: ptToHalfPoints(fontConfig.body.size), color: fontConfig.body.color },
        paragraph: { spacing: { line: lineSpacing('body'), before: 0, after: SAP_DOCX_PT(6) }, widowControl: true },
      },
      {
        id: 'CFrontMatter', name: 'C-Front Matter', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 1, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.heading.name, size: ptToHalfPoints(fontConfig.heading.size), bold: true, color: fontConfig.heading.color },
        paragraph: { spacing: { after: SAP_DOCX_PT(12), before: 0 }, keepNext: true },
      },
      {
        // Custom "C-Heading N" IDs, not the built-in "HeadingN" ones — docx's Document
        // constructor merges `{ ...defaultStyles, ...options.styles }` (a SHALLOW spread:
        // node_modules/docx/dist/index.cjs ~17297-17298), so this paragraphStyles array
        // completely REPLACES docx's default one instead of merging with it, meaning the
        // built-in Heading1-6 style IDs are undefined here regardless of what we call ours.
        // Heading paragraphs therefore use `style: 'CHeadingN'` + an explicit `outlineLevel`
        // (not the `heading:` shorthand, which sets pStyle to the built-in IDs) so Word's
        // outline/TOC scanning (useOutlineLevels below) still finds them — same pattern as
        // Protocol's DOCX export (DocumentEditor.jsx).
        id: 'CHeading1', name: 'C-Heading 1', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 2, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.heading.name, size: ptToHalfPoints(fontConfig.heading.size), bold: true, color: fontConfig.heading.color },
        paragraph: { spacing: { after: SAP_DOCX_PT(6), before: SAP_DOCX_PT(18) }, keepNext: true },
      },
      {
        id: 'CHeading2', name: 'C-Heading 2', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 3, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.subheading.name, size: ptToHalfPoints(fontConfig.subheading.size), bold: true, color: fontConfig.subheading.color },
        paragraph: { spacing: { after: SAP_DOCX_PT(6), before: SAP_DOCX_PT(12) }, keepNext: true },
      },
      {
        id: 'CHeading3', name: 'C-Heading 3', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 4, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.heading3.name, size: ptToHalfPoints(fontConfig.heading3.size), bold: true, color: fontConfig.heading3.color },
        paragraph: { spacing: { after: SAP_DOCX_PT(3), before: SAP_DOCX_PT(12) }, keepNext: true },
      },
      {
        id: 'CHeading4', name: 'C-Heading 4', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 5, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.heading4.name, size: ptToHalfPoints(fontConfig.heading4.size), bold: true, color: fontConfig.heading4.color },
        paragraph: { spacing: { after: SAP_DOCX_PT(3), before: SAP_DOCX_PT(6) }, keepNext: true },
      },
      {
        id: 'CBodyText', name: 'C-Body Text', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 20, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.body.name, size: ptToHalfPoints(fontConfig.body.size), color: fontConfig.body.color },
        paragraph: { spacing: { line: lineSpacing('body'), before: 0, after: SAP_DOCX_PT(6) }, widowControl: true },
      },
      {
        id: 'CCaption', name: 'C-Caption', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 21, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.caption.name, size: ptToHalfPoints(fontConfig.caption.size), bold: true, color: fontConfig.caption.color },
        paragraph: { spacing: { line: 240, before: SAP_DOCX_PT(6), after: SAP_DOCX_PT(6) }, keepNext: true, widowControl: true },
      },
      {
        id: 'CTableHeader', name: 'C-Table Header', basedOn: 'Normal', next: 'CTableText',
        uiPriority: 22, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.tableHeader.name, size: ptToHalfPoints(fontConfig.tableHeader.size), bold: true, color: fontConfig.tableHeader.color },
        paragraph: { spacing: { line: 240, before: 0, after: 0 } },
      },
      {
        id: 'CTableText', name: 'C-Table Text', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 23, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.tableCell.name, size: ptToHalfPoints(fontConfig.tableCell.size), color: fontConfig.tableCell.color },
        paragraph: { spacing: { line: 240, before: 0, after: 0 } },
      },
      {
        id: 'CBulletList', name: 'C-Bullet List', basedOn: 'Normal', next: 'CBulletList',
        uiPriority: 24, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.body.name, size: ptToHalfPoints(fontConfig.body.size), color: fontConfig.body.color },
        paragraph: {
          spacing: { line: lineSpacing('body'), before: 0, after: 80 },
          indent: { left: SAP_DOCX_CM_TO_DXA(0.63), hanging: SAP_DOCX_CM_TO_DXA(0.63) },
        },
      },
      {
        id: 'CFootnote', name: 'C-Footnote', basedOn: 'Normal', next: 'CBodyText',
        uiPriority: 25, unhideWhenUsed: true, quickFormat: true,
        run: { font: fontConfig.footnote.name, size: ptToHalfPoints(fontConfig.footnote.size), color: fontConfig.footnote.color },
        paragraph: { spacing: { line: 240, before: 0, after: 0 } },
      },
    ],
  };
}

function blocksToDocxChildren(blocks, fontConfig = REGULATORY_FONT_CONFIG) {
  const children = [];
  if (!Array.isArray(blocks)) return children;

  blocks.forEach((b, idx) => {
    if (b.type === 'heading') {
      const lvl = Math.min(b.level || 1, 4);
      const hf = fontConfigForHeadingLevel(fontConfig, lvl);
      children.push(
        new Paragraph({
          // style: 'CHeadingN' (not the built-in `heading:` shorthand) so Word shows our
          // actual "C-Heading N" style with the SAP font config, not its own generic
          // Heading N look. outlineLevel makes it participate in the TOC field's
          // useOutlineLevels scan below despite not being on a built-in Heading style.
          style: `CHeading${lvl}`,
          outlineLevel: lvl - 1,
          alignment: AlignmentType.LEFT,
          spacing: {
            line: Math.round((hf.lineSpacing || 1.15) * 240),
            before: SAP_DOCX_PT(lvl === 1 ? 18 : lvl === 2 ? 12 : 6),
            after: SAP_DOCX_PT(lvl >= 3 ? 3 : 6),
          },
          children: [
            new TextRun({
              text: b.text || '',
              font: hf.name,
              size: ptToHalfPoints(hf.size),
              bold: hf.bold,
              color: hf.color,
            }),
          ],
        })
      );
    } else if (b.type === 'paragraph') {
      // A standalone paragraph immediately preceding a table that already reads like a table
      // caption (e.g. "Table 3-1: Summary of...") gets restyled/title-cased per the caption
      // rules; numbering is preserved as authored, not invented.
      const plainText = (b.runs || []).map((r) => r.text || '').join('');
      const caption = blocks[idx + 1]?.type === 'table' ? parseTableCaptionLine(plainText) : null;
      if (caption) {
        const cap = fontConfig.caption || fontConfig.body;
        children.push(
          new Paragraph({
            style: 'CCaption',
            alignment: AlignmentType.LEFT,
            spacing: { before: SAP_DOCX_PT(6), after: SAP_DOCX_PT(6) },
            children: [
              new TextRun({
                text: `${caption.label} `,
                font: cap.name,
                size: ptToHalfPoints(cap.size),
                bold: true,
                color: cap.color,
              }),
              new TextRun({
                text: caption.title,
                font: cap.name,
                size: ptToHalfPoints(cap.size),
                bold: true,
                color: cap.color,
              }),
            ],
          })
        );
        return;
      }
      const bf = fontConfig.body;
      const runs = (b.runs || [{ text: '' }]).map(
        (r) =>
          new TextRun({
            text: r.text || '',
            bold: !!r.bold,
            italics: !!r.italic,
            font: bf.name,
            size: ptToHalfPoints(bf.size),
            color: bf.color,
          })
      );
      children.push(
        new Paragraph({
          style: 'CBodyText',
          alignment: AlignmentType.JUSTIFIED,
          spacing: { line: Math.round((bf.lineSpacing || 1.15) * 240), before: SAP_DOCX_PT(3), after: SAP_DOCX_PT(3) },
          children: runs,
        })
      );
    } else if (b.type === 'list') {
      const bf = fontConfig.body;
      (b.items || []).forEach((runs) => {
        const text = (runs || []).map((r) => r.text || '').join('');
        children.push(
          new Paragraph({
            style: 'CBulletList',
            alignment: AlignmentType.LEFT,
            spacing: { line: Math.round((bf.lineSpacing || 1.15) * 240), before: 0, after: 0 },
            children: [new TextRun({ text, font: bf.name, size: ptToHalfPoints(bf.size), color: bf.color })],
            bullet: { level: 0 },
          })
        );
      });
    } else if (b.type === 'table') {
      // Skip rows with zero cells: a <w:tr> with no <w:tc> children is
      // invalid OOXML and makes Word report the whole document as
      // unreadable/corrupt. This happens for stray/malformed <tr> markup
      // (e.g. an empty separator row) that the source HTML can contain.
      const headerFont = fontConfig.tableHeader;
      const cellFont = fontConfig.tableCell;
      const rows = (b.rows || [])
        .filter((row) => Array.isArray(row) && row.length > 0)
        .map(
          (row, rowIdx) => {
            const isHeaderRow = rowIdx === 0;
            const rowFont = isHeaderRow ? headerFont : cellFont;
            return new TableRow({
              tableHeader: isHeaderRow,
              children: row.map(
                (cellRuns) =>
                  new TableCell({
                    // rowSpan/colSpan (from richTextToBlocks' table parsing) preserve a
                    // merged Objective Type cell across Secondary/Exploratory rows — docx's
                    // own Table builder auto-inserts the vertical-merge continuation cells
                    // into the following rows from these, so omitting them here is what
                    // used to shift each spanned row's remaining cells one column left.
                    ...(cellRuns.rowSpan > 1 ? { rowSpan: cellRuns.rowSpan } : {}),
                    ...(cellRuns.colSpan > 1 ? { columnSpan: cellRuns.colSpan } : {}),
                    margins: sapTableCellMarginsDocx(),
                    verticalAlign: VerticalAlignTable.CENTER,
                    borders: sapTableCellBordersDocx(),
                    children: [
                      new Paragraph({
                        style: isHeaderRow ? 'CTableHeader' : 'CTableText',
                        alignment: AlignmentType.LEFT,
                        spacing: { line: 240, before: 0, after: 0 },
                        children: cellRunsToDisplayRuns(cellRuns, isHeaderRow).map(
                          (r) =>
                            new TextRun({
                              text: r.text || '',
                              bold: isHeaderRow ? true : !!r.bold,
                              italics: !!r.italic,
                              font: rowFont.name,
                              size: ptToHalfPoints(rowFont.size),
                              color: rowFont.color,
                            })
                        ),
                      }),
                    ],
                  })
              ),
            });
          }
        );
      if (rows.length > 0) {
        children.push(
          new Table({
            rows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
      }
    }
  });

  return children;
}

// Strips an optional leading "N. " section-number prefix and normalizes case/
// whitespace, so a title rename in sapSectionDataUsdm.js (e.g. the old
// "0. Document Control" -> the current unnumbered "Document Control") still
// matches a section whose DB record was created — and its title persisted —
// before that rename. Sections are created once per project (see
// fetchSections' "no sections yet" branch) and never re-synced against the
// live DEFAULT_SAP_USDM_SECTIONS afterward, so an existing project's stored
// title can silently drift out of sync with a later title edit.
function normalizeSectionTitleForMatch(title) {
  return String(title || '').replace(/^\d+\.\s*/, '').trim().toLowerCase();
}

// Helper to get the default prompt for a section title
let defaultPromptMap = null;
async function getDefaultPromptForSection(title, projectId) {
  if (!defaultPromptMap) {
    const sections = await getPredefinedSapUsdmSections(projectId);
    defaultPromptMap = {};
    for (const s of sections) {
      defaultPromptMap[s.title] = s.prompt;
    }
  }
  if (defaultPromptMap[title] != null) return defaultPromptMap[title];

  // Exact title not found (e.g. a stale pre-rename title stored on an
  // existing project's section) — fall back to a normalized match rather
  // than silently sending an empty default prompt (which is what "Regenerate"
  // was doing for any already-created Document Control section: the model
  // got no section-specific instructions at all, just whatever it improvised
  // from the context alone).
  const target = normalizeSectionTitleForMatch(title);
  const matchedKey = Object.keys(defaultPromptMap).find(
    (key) => normalizeSectionTitleForMatch(key) === target
  );
  return matchedKey ? defaultPromptMap[matchedKey] : '';
}

const SAPEditorUsdm = () => {
  const navigate = useNavigate();
  const { id: documentId } = useParams(); // Using documentId from URL params for specific document fetches
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  // Determine the actual projectId to use
  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [sections, setSections] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isGenerating, setIsGenerating] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableSectionTitles, setAvailableSectionTitles] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [mode, setMode] = useState('preview'); // 'edit' or 'preview'
  const [editedTextContent, setEditedTextContent] = useState({}); // Track edited text content
  const [editedPromptContent, setEditedPromptContent] = useState({}); // Local prompt buffer, committed on blur

  // Per-section debounce timers for saving edited content HTML (avoid a
  // network call per keystroke) — same pattern as DocumentEditor.jsx.
  const sectionSaveTimers = useRef({});
  // Anchors for each section card, so the Document Overview chips can scroll
  // straight to a section — same navigation pattern as the Protocol/ICF canvas.
  const sectionRefs = useRef({});
  // Bumped whenever a section's content changes from something OTHER than
  // local typing (Generate/Regenerate, Revert to Version) — used as part of
  // EditableSectionContent's resetKey so the editable box actually refreshes
  // to show new server-provided content. It must NOT change from typing
  // alone, or every keystroke would reset the DOM and destroy the caret.
  const [contentSyncNonce, setContentSyncNonce] = useState({});
  const bumpContentSyncNonce = (_id) => {
    setContentSyncNonce((prev) => ({ ...prev, [_id]: (prev[_id] || 0) + 1 }));
  };

  // Open Questions: { [sectionId]: string[] } detected in that section's
  // current output. openQuestionsDialogSectionId is the section currently
  // shown in the answer dialog (null = closed). openQuestionAnswers holds
  // draft answers keyed by question index while the dialog is open.
  const [openQuestionsBySection, setOpenQuestionsBySection] = useState({});
  const [openQuestionsDialogSectionId, setOpenQuestionsDialogSectionId] = useState(null);
  const [openQuestionAnswers, setOpenQuestionAnswers] = useState({});
  const [isResolvingOpenQuestions, setIsResolvingOpenQuestions] = useState(false);

  const syncOpenQuestions = (_id, html) => {
    const found = extractOpenQuestions(html);
    setOpenQuestionsBySection((prev) => ({ ...prev, [_id]: found }));
  };

  // USDM-sourced study facts, injected into every section's generation prompt
  const [usdmFactsText, setUsdmFactsText] = useState('');
  const [usdmNotice, setUsdmNotice] = useState(null); // shown when USDM data is unavailable for this project

  // Progress for "Generate All Sections": null when not running, otherwise
  // { current, total, title } updated as each section finishes in turn.
  const [bulkGenerateProgress, setBulkGenerateProgress] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Feedback for the eSAP XML export's data-layer push, since there's no
  // notification channel in this app that could deliver that result later —
  // this is the only place the outcome is ever shown.
  const [xmlPushSnackbar, setXmlPushSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const closeXmlPushSnackbar = () => setXmlPushSnackbar((prev) => ({ ...prev, open: false }));

  // KPI: user-perceived editor load (route open → sections ready)
  useEffect(() => {
    startSpan('ui_sap_editor_load', { page: 'sap_editor', meta: { projectId: actualProjectId || projectIdFromUrl || null } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !projectContextLoading && actualProjectId) {
      endSpan('ui_sap_editor_load', { status: error ? 'error' : 'ok' });
    }
  }, [isLoading, projectContextLoading, actualProjectId, error]);

  // State for Add Section dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [customSectionTitle, setCustomSectionTitle] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 for Template, 1 for Custom

  // Add to SAPEditor state:
  const [sectionTabs, setSectionTabs] = useState({}); // { [sectionId]: 0 for Edit, 1 for Version History }
  const [sectionVersions, setSectionVersions] = useState({}); // { [sectionId]: [versions] }
  const [trialSchemaImageUrl, setTrialSchemaImageUrl] = useState(null); // Trial schema image for Section 2 preview

  // Title Page (front matter, rendered as its own page in the DOCX/PDF export —
  // see buildSapTitlePageDocxChildren/buildSapTitlePagePdfBlocks) — a separate
  // structured record (SapTitlePage), not a generated SAP section.
  const [titlePageExpanded, setTitlePageExpanded] = useState(false);
  const [titlePageData, setTitlePageData] = useState(null);
  const [titlePageLoading, setTitlePageLoading] = useState(false);
  const [titlePageSaving, setTitlePageSaving] = useState(false);
  const [titlePageAutoFilling, setTitlePageAutoFilling] = useState(false);
  const [titlePageError, setTitlePageError] = useState(null);

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      console.log('SAPEditor: Loading project from URL:', projectIdFromUrl);
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  // Keyboard shortcut to toggle preview mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        setMode(mode === 'edit' ? 'preview' : 'edit');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode]);

  // Fetch USDM study-definition data for this project, extract SAP-relevant
  // facts, and format them for prompt injection. Resolves to '' (with an
  // inline notice) if the project has no registered protocol yet, so
  // generation still proceeds using intake/protocol context alone.
  const fetchUsdmContext = async () => {
    try {
      if (!actualProjectId) return '';
      const response = await protocolRegistrationService.getUsdmByProject(actualProjectId);
      // Fallback only: covers the case where the USDM record's own
      // organizations/studyIdentifiers are empty (protocol XML header wasn't
      // filled in) — Protocol's own UI/export already reads Sponsor Name /
      // Sponsor Protocol Identifier from the project record instead of the
      // USDM record, so SAP generation should have access to the same facts.
      const facts = extractUsdmFacts(response?.usdm, getProjectIdentityFields(currentProject));
      const formatted = formatUsdmFactsForPrompt(facts);
      setUsdmFactsText(formatted);
      setUsdmNotice(null);
      return formatted;
    } catch (err) {
      console.warn('USDM data unavailable for SAP generation:', err?.response?.data?.message || err.message);
      setUsdmFactsText('');
      setUsdmNotice('USDM data unavailable — complete protocol registration to enable USDM-informed generation. Generating from SAP intake data only.');
      return '';
    }
  };

  const fetchTitlePage = async () => {
    if (!actualProjectId) return;
    setTitlePageLoading(true);
    setTitlePageError(null);
    try {
      const result = await documentEditorService.getSapUsdmTitlePage(actualProjectId);
      setTitlePageData(result?.titlePage || null);
    } catch (err) {
      console.error('Error fetching SAP title page:', err);
      setTitlePageError(err?.message || 'Failed to load title page.');
    } finally {
      setTitlePageLoading(false);
    }
  };

  useEffect(() => {
    if (actualProjectId) fetchTitlePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProjectId]);

  const handleTitlePageFieldChange = (field, value) => {
    setTitlePageData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleApproverNameChange = (index, value) => {
    setTitlePageData((prev) => {
      const approvers = [...(prev?.approvers || [])];
      if (approvers[index]) approvers[index] = { ...approvers[index], printedName: value };
      return { ...(prev || {}), approvers };
    });
  };

  const handleSaveTitlePage = async () => {
    if (!actualProjectId || !titlePageData) return;
    setTitlePageSaving(true);
    setTitlePageError(null);
    try {
      const result = await documentEditorService.updateSapUsdmTitlePage(actualProjectId, titlePageData);
      setTitlePageData(result?.titlePage || titlePageData);
    } catch (err) {
      console.error('Error saving SAP title page:', err);
      setTitlePageError(err?.message || 'Failed to save title page.');
    } finally {
      setTitlePageSaving(false);
    }
  };

  // Administrative Info is the M11-title-page form the user already fills in for the
  // protocol — official_title, short_title, sponsor_name/address, nct_number,
  // eu_ct_number, investigational_product_names/codes are authored there directly, not
  // derived — so it's the primary source here, not USDM. USDM facts fill in only Trial
  // Phase (Administrative Info has no phase field) and act as a fallback for anything
  // Administrative Info left blank. Project identity fields (studyId/name/sponsorName)
  // are the same canonical source Protocol's own export uses (projectIdentityFields.js).
  const handleAutoFillTitlePageFromUsdm = async () => {
    if (!actualProjectId) return;
    setTitlePageAutoFilling(true);
    setTitlePageError(null);
    try {
      const [adminResult, usdmResponse, freshProjectResult] = await Promise.all([
        administrativeInfoService.getLatestAdministrativeInfo(actualProjectId).catch((e) => {
          console.warn('Administrative Info unavailable for SAP title page auto-fill:', e?.message || e);
          return null;
        }),
        protocolRegistrationService.getUsdmByProject(actualProjectId).catch((e) => {
          console.warn('USDM data unavailable for SAP title page auto-fill:', e?.message || e);
          return null;
        }),
        // Re-fetched rather than reading `currentProject` from context: that context only
        // refreshes when loadProject() is explicitly called, so a Trial Acronym/Sponsor Name
        // saved moments earlier via Study Registration (which updates its own local form
        // state, not the shared ProjectContext) would otherwise still look blank here.
        projectService.getProject(actualProjectId).catch((e) => {
          console.warn('Project record unavailable for SAP title page auto-fill:', e?.message || e);
          return null;
        }),
      ]);
      const admin = adminResult?.data || null;
      const identity = getProjectIdentityFields(freshProjectResult?.data || currentProject);
      const facts = extractUsdmFacts(usdmResponse?.usdm, identity);

      const interventionNames = (facts?.armsAndEpochs?.interventions || [])
        .map((iv) => iv.name)
        .filter(Boolean)
        .join(', ');
      // Deliberately NOT facts.identity.resolvedTitle: that field falls back to the full
      // design-description paragraph when the record's title fields look unreliable (e.g.
      // only a study code was supplied) — the right fallback for a narrative SAP section,
      // but a paragraph-length "title" here. officialTitle/briefTitle are the real,
      // short title fields with no such fallback.
      const usdmFullTitle = facts?.identity?.officialTitle || '';
      const usdmBriefTitle = facts?.identity?.briefTitle || '';
      // USDM's own identifiers list (e.g. "ClinicalTrials.gov: NCT01234567") when the
      // record actually carries registry numbers as structured StudyIdentifiers.
      const usdmIdentifiers = facts?.identity?.identifiers || [];
      const stripIdentifierScope = (s) => String(s || '').replace(/^.*?:\s*/, '').trim();
      const usdmNctNumber = stripIdentifierScope(usdmIdentifiers.find((s) => /nct/i.test(s)));
      const usdmEudraCtNumber = stripIdentifierScope(
        usdmIdentifiers.find((s) => /eudract|eu[\s-]?ct/i.test(s))
      );
      const sponsorNameAndAddress =
        [admin?.sponsor_name, admin?.sponsor_legal_address].filter(Boolean).join(', ') ||
        identity.sponsorName ||
        (facts?.sponsor?.names || [])[0] ||
        '';

      setTitlePageData((prev) => ({
        ...(prev || {}),
        // Structured USDM title fields first (same reasoning as investigationalProductNames
        // below) — Administrative Info's official_title/short_title only as fallback, since
        // USDM is the trial's own record rather than a separately-typed form field.
        fullTitle: usdmFullTitle || admin?.official_title || prev?.fullTitle || '',
        shortTitle: usdmBriefTitle || admin?.short_title || prev?.shortTitle || '',
        trialPhase: facts?.design?.studyPhase || prev?.trialPhase || '',
        // Project.name/studyId first, not Administrative Info's free-text fields — those
        // can (and did, in practice) hold stale placeholder text like the literal word
        // "Pending" from an old draft submission, silently overriding the real,
        // canonically-maintained value (see projectIdentityFields.js: Trial Acronym and
        // Sponsor Protocol Identifier are stored on the Project record, and the latter is
        // immutable once the trial exists).
        trialAcronym: identity.trialAcronym || admin?.trial_acronym || prev?.trialAcronym || '',
        sponsorProtocolIdentifier:
          identity.sponsorProtocolIdentifier || admin?.sponsor_protocol_identifier || prev?.sponsorProtocolIdentifier || '',
        sponsorProductCodes: admin?.sponsor_investigational_product_codes || prev?.sponsorProductCodes || '',
        // USDM's structured StudyIntervention names first, not Administrative Info's free-text
        // field — that field is required on save so it's always non-empty whenever an
        // Administrative Info record exists, and previously silently overrode the (working)
        // USDM-derived names with whatever was typed there, even if it wasn't the real answer.
        investigationalProductNames: interventionNames || admin?.investigational_product_names || prev?.investigationalProductNames || '',
        nctNumber: usdmNctNumber || admin?.nct_number || prev?.nctNumber || '',
        eudraCtNumber: usdmEudraCtNumber || admin?.eu_ct_number || prev?.eudraCtNumber || '',
        sponsorNameAndAddress: sponsorNameAndAddress || prev?.sponsorNameAndAddress || '',
      }));
    } catch (err) {
      console.error('Error auto-filling SAP title page:', err);
      setTitlePageError(err?.message || 'Failed to auto-fill title page.');
    } finally {
      setTitlePageAutoFilling(false);
    }
  };

  // Fetch available section titles and sections on component mount, or when project changes.
  // Note: this only loads USDM facts and the (possibly empty-placeholder)
  // section list — it does not generate any content. Generation is always
  // user-triggered (per-section "Generate Content", or "Generate All Sections").
  useEffect(() => {
    if (actualProjectId && !projectContextLoading) {
      fetchUsdmContext();
      fetchSectionTitles();
      fetchSections();
      fetchTrialSchemaImage();
    } else if (!actualProjectId && !projectContextLoading) {
      setError('Please select a project to view its documents.');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProjectId, projectContextLoading]); // Re-run when actualProjectId or projectContextLoading changes

  // Fetch trial schema image (same source as protocol/clinical intake)
  const fetchTrialSchemaImage = async () => {
    try {
      if (!actualProjectId) return;
      const response = await clinicalIntakeService.fetchLatestClinicalIntakeWithTrialSchemaFromApi(actualProjectId);
      if (!response) {
        setTrialSchemaImageUrl(null);
        return;
      }
      const trialSchemaFiles = response?.document_uploads?.uploaded_files?.trial_schema || [];
      if (trialSchemaFiles.length > 0) {
        const file = trialSchemaFiles[0];
        const imageUrl = file.fileUrl || file.azureBlobUrl || file.url || null;
        setTrialSchemaImageUrl(imageUrl);
      } else if (response.trialSchemaSvg) {
        setTrialSchemaImageUrl(response.trialSchemaSvg);
      } else {
        setTrialSchemaImageUrl(null);
      }
    } catch (err) {
      console.error('Error fetching trial schema image for SAP:', err);
      setTrialSchemaImageUrl(null);
    }
  };

  // Fetch available section titles
  const fetchSectionTitles = async () => {
    try {
      const titles = await getPredefinedSapUsdmSections(actualProjectId); // Pass projectId
      setAvailableSectionTitles(titles);
      setSelectedSectionTitle(titles[0] || ''); // Set default to first available
    } catch (err) {
      console.error('Error fetching SAP section titles:', err);
      setError('Failed to load available SAP section titles.');
    }
  };

  // Fetch sections. Does NOT auto-generate any content — when a project has
  // no SAP-USDM sections yet, this only creates empty placeholder rows (one
  // per predefined section, in template order) so the user sees the full
  // section list immediately and can trigger generation explicitly, either
  // per-section or via "Generate All Sections" (which runs one section at a
  // time and updates the UI as each finishes — see handleGenerateAllSections).
  const fetchSections = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedSections = await documentEditorService.getAllSAPUsdmSections(actualProjectId);

      if (fetchedSections && fetchedSections.length > 0) {
        // Self-heal titles that were persisted before a later rename in
        // sapSectionDataUsdm.js (e.g. old "0. Document Control" -> current
        // "Document Control") — otherwise this project would show the stale
        // title forever, since sections are only ever created once per
        // project (see below) and never re-synced against the live template.
        const canonicalSections = await getPredefinedSapUsdmSections(actualProjectId);
        const canonicalTitles = new Set(canonicalSections.map((s) => s.title));
        const reconciledSections = await Promise.all(
          fetchedSections.map(async (s) => {
            if (canonicalTitles.has(s.title)) return s;
            const renameTarget = canonicalSections.find(
              (c) => normalizeSectionTitleForMatch(c.title) === normalizeSectionTitleForMatch(s.title)
            );
            if (!renameTarget) return s; // no known rename match — leave the stored title alone
            try {
              await documentEditorService.updateSAPUsdmSection(s._id, { title: renameTarget.title }, actualProjectId);
              return { ...s, title: renameTarget.title };
            } catch (err) {
              console.warn('Failed to reconcile renamed section title:', s.title, '->', renameTarget.title, err);
              return s;
            }
          })
        );
        setSections(reconciledSections);
        // Scan already-generated content (from a previous session) for open
        // questions so their badges show up immediately, not just after a
        // fresh generation.
        reconciledSections.forEach((s) => {
          if (s.output && s.output.trim()) syncOpenQuestions(s._id, s.output);
        });
        return;
      }

      // No sections yet for this project — create empty placeholders for
      // each predefined section so the list renders immediately.
      const predefinedSections = await getPredefinedSapUsdmSections(actualProjectId);
      for (const section of predefinedSections) {
        try {
          await documentEditorService.createSAPUsdmSection({
            title: section.title,
            context: section.context || '',
            prompt: '', // Always empty for the user; default prompt is applied at generation time
            output: '',
            projectId: actualProjectId
          }, actualProjectId);
        } catch (createErr) {
          console.error('Failed to create SAP-USDM section placeholder:', createErr);
          // Continue creating the remaining placeholders even if one fails
        }
      }

      // Re-fetch from the server as the source of truth (rather than
      // accumulating local results), so concurrent mount effects (e.g. React
      // StrictMode double-invoking this effect in development) can't leave
      // the UI showing a partial/inconsistent list.
      const updatedSections = await documentEditorService.getAllSAPUsdmSections(actualProjectId);
      setSections(updatedSections);
    } catch (err) {
      setError('Failed to load SAP sections.');
      console.error('Error fetching SAP sections:', err);
    } finally {
      setIsLoading(false);
    }
  };


  // Helper to fetch versions for a section
  const fetchSectionVersions = async (sectionId) => {
    try {
      const versionsData = await documentEditorService.getAllSAPUsdmVersions(sectionId, actualProjectId);
      setSectionVersions(prev => ({ ...prev, [sectionId]: versionsData.versions || [] }));
    } catch (err) {
      console.error('Error fetching SAP section versions:', err);
      setSectionVersions(prev => ({ ...prev, [sectionId]: [] }));
    }
  };

  const handleInputChange = async (_id, field, value) => {
    // Functional update: reads the latest sections state at apply-time
    // rather than closing over whatever `sections` was when this call was
    // scheduled. Matters during "Generate All Sections", where several
    // handleInputChange calls are in flight/queued across a sequential loop
    // captured from a single stale closure — using `sections` directly here
    // would make each update overwrite the previous section's just-generated
    // output with the pre-loop snapshot (visible as content "flashing" in
    // and then reverting to empty as the next section generates).
    setSections((prevSections) =>
      prevSections.map((section) =>
        section._id === _id ? { ...section, [field]: value } : section
      )
    );
    try {
      await documentEditorService.updateSAPUsdmSection(_id, { [field]: value }, actualProjectId); // Pass projectId
    } catch (error) {
      console.error('Error updating SAP section:', error);
    }
  };

  // Persist a section's edited HTML as-is (already sanitized by
  // EditableSectionContent) — no flatten-to-text/rewrap round-trip, so
  // headings, tables, bold/italic, and lists the user didn't touch stay
  // intact. Same debounced-save pattern as DocumentEditor.jsx's
  // handleTextContentChange: local state updates immediately for
  // responsive typing, the network save is debounced 500ms so rapid typing
  // doesn't fire (and version) a save per keystroke.
  const handleTextContentChange = (_id, newHtml) => {
    setEditedTextContent(prev => ({ ...prev, [_id]: newHtml }));
    setSections(prevSections =>
      prevSections.map(section =>
        section._id === _id ? { ...section, output: newHtml } : section
      )
    );
    // Re-scan immediately (no need to wait for the debounced save below) so
    // manually editing away an "Open Question" note clears its badge right away.
    syncOpenQuestions(_id, newHtml);

    if (sectionSaveTimers.current[_id]) {
      clearTimeout(sectionSaveTimers.current[_id]);
    }
    sectionSaveTimers.current[_id] = setTimeout(async () => {
      try {
        await documentEditorService.updateSAPUsdmSection(_id, { output: newHtml }, actualProjectId);
      } catch (err) {
        console.error('Failed to update SAP section text content:', err);
      }
    }, 500);
  };

  // priorOutputsOverride: optional { [title]: output } map used by
  // handleGenerateAllSections so Section 13 can see sections generated
  // earlier in the same bulk run, without waiting on React state to settle.
  // openQAOverride: optional [{ question, answer }] — when the user has just
  // answered previously-flagged open questions for this section, their
  // answers are layered into the context so this regeneration resolves them
  // instead of repeating the same gaps.
  const handleGenerateContent = async (_id, priorOutputsOverride, openQAOverride) => {
    setIsGenerating((prev) => ({ ...prev, [_id]: true }));
    setError(null);
    try {
      const sectionToGenerate = sections.find((s) => s._id === _id);
      if (!sectionToGenerate) {
        throw new Error('SAP section not found for content generation.');
      }
      if (sectionToGenerate.final) {
        setError('This section is locked (marked final). Unlock it first to generate new content.');
        return null;
      }

      // The prompt field only persists to `sections` state on blur, so a
      // prompt typed and then immediately submitted via Generate/Regenerate
      // could still be sitting in the
      // local editedPromptContent buffer and not yet reflected in
      // sectionToGenerate.prompt. Read the buffer here so generation always
      // uses what's actually in the box. This is NOT persisted separately —
      // it's saved together with the generated output in a single update
      // once generation finishes, so one Regenerate click produces exactly
      // one version-history entry (new prompt + new output together)
      // instead of two (an intermediate "new prompt, still-old output"
      // version followed by the real one).
      const bufferedPrompt = editedPromptContent[_id];
      const promptChanged = bufferedPrompt !== undefined && bufferedPrompt !== (sectionToGenerate.prompt || '');
      const effectivePrompt = promptChanged ? bufferedPrompt : sectionToGenerate.prompt;

      // A custom prompt is a modification request ("remove the open
      // questions", "don't say USDM"), not a full replacement system prompt
      // — it must be layered ON TOP of the section's default prompt, not
      // substituted for it. Using it alone would throw away the required
      // subsection structure, the "never invent a number" discipline, and
      // the USDM-fact-usage guidance, leaving the model to freelance a
      // different structure with none of those constraints.
      const defaultPrompt = await getDefaultPromptForSection(sectionToGenerate.title, actualProjectId);
      let promptToUse = defaultPrompt;
      if (effectivePrompt && effectivePrompt.trim()) {
        promptToUse = `${defaultPrompt}\n\nADDITIONAL INSTRUCTIONS FOR THIS GENERATION (apply these on top of everything above — keep the required subsections and discipline rules unless an instruction explicitly says otherwise):\n${effectivePrompt.trim()}`;
      }

      // Build context for Section 2, 3, 4 using protocol sections
      let contextToUse = sectionToGenerate.context || '';
      if (sectionToGenerate.title === '2. Study Context') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 2: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                // 2.7 Planned Sample Size needs the protocol's own sample size
                // figure — the USDM-mapped facts alone are often too thin for
                // this (see usdmToSapContext.js's narrative-fallback comment).
                // Listed BEFORE the trial-design fetch below: the
                // "decentralized trial elements" truncation further down only
                // trims what comes AFTER that phrase in the joined string, so
                // ordering this first keeps it out of that cut entirely.
                '10. STATISTICAL CONSIDERATIONS',
                '4.1 Description of Trial Design',
              ],
            },
          ];
          let protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            const idx = protocolContext.toLowerCase().indexOf('decentralized trial elements'.toLowerCase());
            if (idx !== -1) {
              protocolContext = protocolContext.substring(0, idx);
            }
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 2 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 2 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '3. Statistical Framework') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 3: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                // 3.7 Sample Size Determination needs this — see the matching
                // note on Section 2's protocolSources above.
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 3 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 3 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '4. ENDPOINTS AND BASELINE VARIABLES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 4: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 4 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 4 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '5. HYPOTHESES AND SAMPLE SIZE CONSIDERATIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 5: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 5 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 5 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '6. ANALYSIS SETS (POPULATIONS FOR ANALYSIS)') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 6: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10.2 Analysis Sets',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 6 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 6 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '7. GENERAL STATISTICAL METHODOLOGY AND CONVENTIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 7: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 7 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 7 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '8. BASELINE, STUDY CONDUCT, AND PARTICIPANT CHARACTERISTICS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 8: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '1. PROTOCOL SUMMARY',
                '2. INTRODUCTION',
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '4. TRIAL DESIGN',
                '5. TRIAL POPULATION',
                '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
                '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
                '10. STATISTICAL CONSIDERATIONS',
                '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
                '12. APPENDIX: SUPPORTING DETAILS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== FULL PROTOCOL CONTEXT FOR SECTION 8 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 8 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '9. EFFICACY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 9: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 9 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 9 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '10. SAFETY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 10: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '12. APPENDIX: SUPPORTING DETAILS',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 10 (SAFETY) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 10 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '11. PHARMACOKINETIC (PK) ANALYSES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 11: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 11 (PK) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 11 generation:', e);
        }
        contextToUse = parts.join('');
      }

      if (sectionToGenerate.title === '13. Appendices') {
        const priorEntries = priorOutputsOverride
          ? Object.entries(priorOutputsOverride)
          : sections.filter((s) => s.title !== '13. Appendices' && s.output).map((s) => [s.title, s.output]);
        const priorContent = priorEntries
          .filter(([title]) => title !== '13. Appendices')
          .map(([title, output]) => `=== ${title} ===\n${output}`)
          .join('\n\n');
        contextToUse += `\n\nPREVIOUSLY GENERATED SAP SECTIONS:\n${priorContent || '(no prior section content available)'}`;
      }

      if (Array.isArray(openQAOverride) && openQAOverride.length > 0) {
        const qaText = openQAOverride
          .filter((qa) => qa && qa.answer && qa.answer.trim())
          .map((qa) => `Q: ${qa.question}\nA: ${qa.answer.trim()}`)
          .join('\n\n');
        if (qaText) {
          contextToUse += `\n\nANSWERS TO PREVIOUSLY OPEN QUESTIONS (use these facts to resolve the corresponding open items below; remove the "Open Question" note for anything now answered, and do not invent an answer for anything left unanswered):\n${qaText}`;
        }
      }

      // generateContent's signature is (prompt, context, output, sectionType,
      // sectionTitle, sectionData, styleOpts, usdmContext) — usdmFactsText
      // belongs in the 8th slot (usdmContext), not the 7th (styleOpts). It was
      // previously passed as the 7th argument, so it silently landed in
      // styleOpts; since it's a string (not `typeof === 'object'`), it was
      // dropped there with no error, and usdmContext defaulted to null — so
      // the server (ai.service.js) never received ANY USDM facts (sponsor,
      // objectives, arms, population, etc.) for SAP generation.
      const generatedOutput = unboldCrossReferenceSentences(
        await generateContent(
          promptToUse,
          contextToUse,
          '',
          'sap',
          sectionToGenerate.title,
          null,
          null,
          usdmFactsText
        )
      );
      // Save the generated output — and the new prompt, if one was typed —
      // together as a single update, so this creates exactly one version.
      const updatePayload = { output: generatedOutput, provenance: 'aidrafted' };
      if (promptChanged) updatePayload.prompt = effectivePrompt;
      try {
        await documentEditorService.updateSAPUsdmSection(_id, updatePayload, actualProjectId);
      } catch (saveErr) {
        console.error('Error saving generated SAP content:', saveErr);
      }
      setSections((prevSections) =>
        prevSections.map((s) => (s._id === _id ? { ...s, ...updatePayload } : s))
      );
      // Always clear the prompt buffer after a generation, not just when
      // promptChanged is true. Clicking Generate/Regenerate blurs the prompt
      // field first (focus moves to the button), which can independently
      // sync sectionToGenerate.prompt via the field's own onBlur handler
      // before this closure's `promptChanged` check runs — making that
      // check see "no change" even though the box still has the just-typed
      // text sitting in it. Clearing unconditionally means the box always
      // goes blank once its contents have been used, regardless of that race.
      setEditedPromptContent((prev) => {
        if (!(_id in prev)) return prev;
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
      // Clear edited text content for this section
      setEditedTextContent(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
      // Force the editable content box to resync with the freshly generated
      // output — its resetKey only changes when this nonce changes, so
      // without this it would keep showing whatever was there before.
      bumpContentSyncNonce(_id);
      // Re-scan for Open Questions in the fresh output — clears the badge if
      // this regeneration (e.g. with just-supplied answers) resolved them,
      // or updates it if new/different ones are present.
      syncOpenQuestions(_id, generatedOutput);
      return generatedOutput;
    } catch (err) {
      console.error('Error generating SAP content:', err);
      setError(`Failed to generate content: ${err.message}`);
      return null;
    } finally {
      setIsGenerating((prev) => ({ ...prev, [_id]: false }));
    }
  };

  // Generates every section that has no output yet, one at a time — each
  // section's result appears in the UI as soon as it finishes (via
  // handleGenerateContent -> handleInputChange -> setSections), rather than
  // generating everything in the background and revealing it all at once.
  const handleGenerateAllSections = async () => {
    const toGenerate = sections.filter((s) => !s.final && (!s.output || !s.output.trim()));
    if (toGenerate.length === 0) return;

    setBulkGenerateProgress({ current: 0, total: toGenerate.length, title: '' });
    const priorOutputs = {};
    sections.forEach((s) => {
      if (s.output && s.output.trim()) priorOutputs[s.title] = s.output;
    });

    for (let i = 0; i < toGenerate.length; i++) {
      const section = toGenerate[i];
      setBulkGenerateProgress({ current: i + 1, total: toGenerate.length, title: section.title });
      const output = await handleGenerateContent(section._id, priorOutputs);
      if (output) priorOutputs[section.title] = output;
    }
    setBulkGenerateProgress(null);
  };


  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all SAP sections for this project? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await documentEditorService.deleteAllSAPUsdmSections(actualProjectId); // Pass projectId
        setSections([]);
        setError(null);
        // Re-fetch predefined sections to re-populate after reset
        await fetchSections();
      } catch (error) {
        console.error('Error resetting SAP sections:', error);
        setError(`Failed to reset SAP sections: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExportSapPdfNative = async () => {
    startSpan('ui_sap_export_pdf_native', { page: 'sap_editor', meta: { projectId: actualProjectId || null } });
    try {
      const printableSections = (sections || [])
        .filter((s) => s && s.title && s.output && String(s.output).trim())
        // Cross-reference sentences are body prose; drop any bold the model
        // baked in so the PDF matches the on-screen preview.
        .map((s) => ({ ...s, output: unboldCrossReferenceSentences(s.output) }));
      if (printableSections.length === 0) {
        alert('No SAP content available to export as PDF.');
        endSpan('ui_sap_export_pdf_native', { status: 'error' });
        return;
      }

      // Title Page + Approvers and Signatories — same front-matter pages as the DOCX
      // export, re-fetched here in case the Title Page tab was never opened this session.
      let titlePageForExport = titlePageData;
      if (!titlePageForExport && actualProjectId) {
        try {
          const result = await documentEditorService.getSapUsdmTitlePage(actualProjectId);
          titlePageForExport = result?.titlePage || null;
        } catch (e) {
          console.warn('Could not load SAP title page for PDF export:', e?.message || e);
        }
      }
      const frontMatterPageCount = titlePageForExport ? 2 : 1; // Title Page + Approvers, or the bare fallback page

      let blob = await pdf(
        <SAPPdfDocument
          sections={printableSections}
          trialSchemaImageUrl={trialSchemaImageUrl}
          titlePage={titlePageForExport}
        />
      ).toBlob();

      // Insert a real, hyperlinked, page-numbered Table of Contents — same approach as
      // the protocol PDF export (see nativeProtocolPdfToc.js), since @react-pdf/renderer
      // has no auto-updating TOC field the way the Word export's TableOfContents does.
      try {
        const tocEntries = buildSapPdfTocEntries(printableSections);
        if (tocEntries.length > 0) {
          const { insertProtocolPdfToc } = await import('../../utils/nativeProtocolPdfToc');
          blob = await insertProtocolPdfToc(blob, tocEntries, { frontMatterPageCount });
        }
      } catch (tocErr) {
        console.warn('SAP native PDF TOC insertion failed; exporting without a TOC:', tocErr?.message || tocErr);
      }

      try {
        const { stampProtocolPdfPageNumbers } = await import('../../utils/stampProtocolPdfPageNumbers');
        blob = await stampProtocolPdfPageNumbers(blob, { skipPages: frontMatterPageCount });
      } catch (pageNumErr) {
        console.warn('SAP native PDF page numbering failed:', pageNumErr?.message || pageNumErr);
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'statistical-analysis-plan.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      endSpan('ui_sap_export_pdf_native', { status: 'ok' });
    } catch (e) {
      console.error('Failed to export SAP as PDF:', e);
      alert('Failed to export SAP as PDF.');
      endSpan('ui_sap_export_pdf_native', { status: 'error' });
    }
  };

  const handleDeleteSection = async (_id) => {
    if (window.confirm('Are you sure you want to delete this SAP section? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await documentEditorService.deleteSAPUsdmSection(_id, actualProjectId); // Pass projectId
        setSections((prev) => prev.filter((section) => section._id !== _id));
        setError(null);
      } catch (error) {
        console.error('Error deleting SAP section:', error);
        setError(`Failed to delete SAP section: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Toggles a section's final/locked state. When final, Generate Content and
  // Regenerate are disabled for that section (see render below) so it can't
  // be silently overwritten; clicking again unlocks it.
  const handleToggleFinal = async (_id) => {
    const current = sections.find((s) => s._id === _id);
    const nextFinal = !current?.final;
    try {
      await documentEditorService.updateSAPUsdmSection(_id, { final: nextFinal }, actualProjectId);
      setSections((prevSections) =>
        prevSections.map((section) =>
          section._id === _id ? { ...section, final: nextFinal } : section
        )
      );
    } catch (err) {
      console.error('Failed to update SAP section final state:', err);
      alert(`Failed to ${nextFinal ? 'mark section as final' : 'unlock section'}.`);
    }
  };

  const handleStyleAutofixApplied = async (sectionId, html) => {
    await documentEditorService.updateSAPUsdmSection(sectionId, { output: html }, actualProjectId);
    setSections((prev) => prev.map((s) => (s._id === sectionId ? { ...s, output: html } : s)));
  };

  const handleExport = async (formatType) => {
    if (formatType === 'word') {
      exportToWord(sections);
    } else if (formatType === 'xml') {
      exportToXML(sections);
    }
  };

  const exportToWord = async (sectionsToExport) => {
    try {
      setIsExporting(true);
      const children = [];

      // Title Page + Approvers and Signatories — dedicated front-matter pages (see the
      // Title Page tab above), rendered before the TOC/Document Control per the SAP
      // title-page template. Re-fetched here in case the tab was never opened this session.
      let titlePageForExport = titlePageData;
      if (!titlePageForExport && actualProjectId) {
        try {
          const result = await documentEditorService.getSapUsdmTitlePage(actualProjectId);
          titlePageForExport = result?.titlePage || null;
        } catch (e) {
          console.warn('Could not load SAP title page for export:', e?.message || e);
        }
      }
      children.push(...buildSapTitlePageDocxChildren(titlePageForExport));
      children.push(...buildSapApproversDocxChildren(titlePageForExport));

      // Dynamic TOC: Word-native TableOfContents using headings from SAP sections
      children.push(...generateSapTableOfContents());

      // Page break before SAP sections start
      children.push(
        new Paragraph({
          children: [],
          pageBreakBefore: true,
        })
      );

      (sectionsToExport || []).forEach((section, idx) => {
        if (!section || !section.title) return;

        if (idx > 0) {
          children.push(
            new Paragraph({
              children: [],
              pageBreakBefore: true,
            })
          );
        }

        children.push(
          new Paragraph({
            // style: 'CHeading1' + outlineLevel — see the comment in blocksToDocxChildren's
            // heading branch for why this uses the custom style instead of `heading:`.
            style: 'CHeading1',
            outlineLevel: 0,
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round((REGULATORY_FONT_CONFIG.heading.lineSpacing || 1.15) * 240),
              before: SAP_DOCX_PT(18),
              after: SAP_DOCX_PT(6),
            },
            children: [
              new TextRun({
                text: section.title,
                font: REGULATORY_FONT_CONFIG.heading.name,
                size: ptToHalfPoints(REGULATORY_FONT_CONFIG.heading.size),
                bold: REGULATORY_FONT_CONFIG.heading.bold,
                color: REGULATORY_FONT_CONFIG.heading.color,
              }),
            ],
          })
        );

        const rawOutput =
          section.output && unboldCrossReferenceSentences(String(section.output).trim());
        if (rawOutput) {
          // Scope heading detection to this section's own number, same as
          // exportToXML does — otherwise the two exports parse the same
          // section.output into different blocks (e.g. text that's part of
          // this section's body gets mis-promoted into a heading/subsection
          // in one export but not the other), so the two documents disagree
          // on what a section's content actually is.
          const titleMatch = (section.title || '').match(/^(\d+)(\.\d+)*/);
          const sectionRootPrefix = titleMatch ? titleMatch[0] : null;
          const blocks = richTextToBlocks(rawOutput, { sectionRootPrefix });
          // The heading above already renders the section title once — drop
          // a leading heading in the generated content if it just restates
          // that same title (e.g. "3.0 STATISTICAL FRAMEWORK" under
          // "3. Statistical Framework"), so it isn't printed twice.
          if (blocks[0]?.type === 'heading' && isDuplicateTitleHeading(blocks[0].text, section.title)) {
            blocks.shift();
          }
          children.push(...blocksToDocxChildren(blocks));
        } else {
          children.push(new Paragraph({ style: 'CBodyText', alignment: AlignmentType.LEFT, text: 'No content generated' }));
        }

        children.push(new Paragraph({ text: '' }));
      });

      const doc = new Document({
        styles: buildSapDocxStyles(REGULATORY_FONT_CONFIG),
        sections: [
          {
            properties: {},
            children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sap-document.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Word:', error);
      setError('Failed to export document to Word format.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToXML = async (sectionsToExport) => {
    // Sort sections by title (e.g., "1. Introduction", "1.1 Purpose")
    const sortedSections = [...sectionsToExport].sort((a, b) => {
      const aTitle = a.title || '';
      const bTitle = b.title || '';
      return aTitle.localeCompare(bTitle, undefined, { numeric: true });
    });

    const identity = getProjectIdentityFields(currentProject);

    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xmlContent +=
      `<StatisticalAnalysisPlan xmlns="${ESAP_SAP_NAMESPACE}" ` +
      `xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ` +
      `xsi:schemaLocation="${ESAP_SAP_NAMESPACE} ${ESAP_SAP_SCHEMA_LOCATION}" ` +
      `schemaVersion="1.0">\n`;
    xmlContent += '  <Header>\n';
    if (identity.sponsorProtocolIdentifier) {
      xmlContent += `    <ProtocolIdentifier>${escapeXml(identity.sponsorProtocolIdentifier)}</ProtocolIdentifier>\n`;
    }
    if (identity.trialAcronym) {
      xmlContent += `    <TrialAcronym>${escapeXml(identity.trialAcronym)}</TrialAcronym>\n`;
    }
    if (identity.sponsorName) {
      xmlContent += `    <Sponsor>${escapeXml(identity.sponsorName)}</Sponsor>\n`;
    }
    xmlContent += `    <GeneratedAt>${new Date().toISOString()}</GeneratedAt>\n`;
    xmlContent += '  </Header>\n';

    let sectionCount = 0;
    for (const section of sortedSections) {
      if (!section.title || !section.output) continue; // Skip if no title or output
      const match = section.title.match(/^(\d+)(\.\d+)*/); // Extract main number (e.g., "1", "1.1")
      const mainNum = match ? match[0] : '';
      const sectionTitle = section.title.replace(/^(\d+(\.\d+)*\.?\s*)/, '').trim(); // Remove number prefix

      const blocks = richTextToBlocks(unboldCrossReferenceSentences(section.output), { sectionRootPrefix: mainNum || null });
      const tree = blocksToSectionTree(blocks, mainNum, sectionTitle);
      xmlContent += serializeSectionNode(tree, 'Section', '  ');
      sectionCount += 1;
    }
    xmlContent += '</StatisticalAnalysisPlan>';

    const filename = 'sap-document.xml';

    // Hand the file to the browser immediately — don't make the download
    // wait on a network round-trip to persist a version.
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Persist this export as the next version for the project. Best-effort:
    // a save failure doesn't undo the download the user already has, but
    // they're told the version wasn't recorded. The save itself now waits on
    // the data-layer push (see sapXmlExport.controller.js), so its result
    // comes back in the same response — shown here since there's no other
    // channel that could tell the user later.
    try {
      const saved = await sapXmlExportService.saveXmlExport(actualProjectId, {
        filename, xmlContent, sectionCount,
      });
      const push = saved.documentServicePush;
      if (push?.status === 'success') {
        setXmlPushSnackbar({
          open: true, severity: 'success',
          message: `Saved version ${saved.versionNumber} and sent it to the data layer.`,
        });
      } else if (push?.status === 'skipped') {
        setXmlPushSnackbar({
          open: true, severity: 'info',
          message: `Saved version ${saved.versionNumber}. Not sent to the data layer: ${push.reason || 'skipped'}`,
        });
      } else if (push?.status === 'failed') {
        setXmlPushSnackbar({
          open: true, severity: 'warning',
          message: `Saved version ${saved.versionNumber}, but sending it to the data layer failed: ${push.error || 'unknown error'}`,
        });
      } else {
        setXmlPushSnackbar({
          open: true, severity: 'success',
          message: `Saved version ${saved.versionNumber}.`,
        });
      }
    } catch (err) {
      console.error('Failed to save SAP XML export version:', err);
      alert('The XML file downloaded, but saving this version to the database failed.');
    }
  };

  const handleViewVersionHistory = (_id) => {
    navigate(`/sap-section-history/${_id}?projectId=${actualProjectId}`);
  };

  const handleAddSection = async () => {
    const title = tabValue === 0 ? selectedSectionTitle : customSectionTitle;
    if (!title) {
      alert('Please select or enter a section title.');
      return;
    }

    try {
      const sectionData = {
        title,
        output: '',
        projectId: actualProjectId,
        documentType: 'SAP' // Add documentType
      };
      const response = await documentEditorService.createSAPUsdmSection(sectionData, actualProjectId);
      setSections((prevSections) => [...prevSections, response.data]);
      handleCloseAddDialog();
      // Re-fetch sections to ensure order and latest data
      fetchSections();
    } catch (error) {
      console.error('Error adding section:', error);
      alert('Failed to add section. It might already exist or there was a server error.');
    }
  };

  const handleRevertVersion = async (sectionId, versionData) => {
    try {
      const updatedSection = await documentEditorService.updateSAPUsdmSection(
        sectionId,
        { output: versionData.output },
        actualProjectId // Pass projectId
      );
      setSections((prevSections) => prevSections.map(s => s._id === sectionId ? { ...s, output: updatedSection.output } : s));
      // Clear any stale local draft and force the editable box to resync
      // with the reverted content, same as after a fresh generation.
      setEditedTextContent(prev => {
        const updated = { ...prev };
        delete updated[sectionId];
        return updated;
      });
      bumpContentSyncNonce(sectionId);
      syncOpenQuestions(sectionId, updatedSection.output);
      alert('SAP section reverted to selected version!');
    } catch (error) {
      console.error('Error reverting SAP section version:', error);
      alert('Failed to revert SAP section version.');
    }
  };

  const handleMenuOpen = (event, _id) => {
    setAnchorEl(event.currentTarget);
    setSelectedSectionId(_id);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Pre-select the first available title or clear custom title
    setSelectedSectionTitle(availableSectionTitles[0] || '');
    setCustomSectionTitle('');
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedSectionTitle('');
    setCustomSectionTitle('');
    setTabValue(0); // Reset to template tab
  };

  const handleCloseOpenQuestionsDialog = () => {
    setOpenQuestionsDialogSectionId(null);
    setOpenQuestionAnswers({});
  };

  const handleSubmitOpenQuestionAnswers = async () => {
    const sectionId = openQuestionsDialogSectionId;
    if (!sectionId) return;
    const questions = openQuestionsBySection[sectionId] || [];
    const qaPairs = questions.map((question, idx) => ({ question, answer: openQuestionAnswers[idx] || '' }));
    if (!qaPairs.some((qa) => qa.answer.trim())) {
      // Nothing answered — just close, no point regenerating for nothing.
      handleCloseOpenQuestionsDialog();
      return;
    }
    setIsResolvingOpenQuestions(true);
    try {
      await handleGenerateContent(sectionId, undefined, qaPairs);
    } finally {
      setIsResolvingOpenQuestions(false);
      handleCloseOpenQuestionsDialog();
    }
  };

  const handleSectionTitleChange = (event) => {
    setSelectedSectionTitle(event.target.value);
    setCustomSectionTitle(''); // Clear custom title if a predefined one is selected
  };

  const handleCustomTitleChange = (event) => {
    setCustomSectionTitle(event.target.value);
    setSelectedSectionTitle(''); // Clear predefined title if a custom one is entered
  };

  if (isLoading || projectContextLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || projectContextError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || projectContextError}</Alert>
      </Box>
    );
  }

  if (!actualProjectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please select a project to view its documents.</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        border: '2px solid #1976d2',
        borderRadius: 4,
        p: 4,
        mt:4,
        background: 'linear-gradient(135deg, #f8fafc 60%, #e3f2fd 100%)',
        boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.18), 0 1.5px 0 #fff inset',
        mb: 4,
        position: 'relative',
        overflow: 'visible',
      }}>
        <Typography variant="h4" gutterBottom>SAP Editor (USDM)</Typography>

        {/* Document Overview: one clickable chip per section, filled once the
            section has content — same navigation panel as the Protocol/ICF
            document editors. */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
            Document Overview
          </Typography>
          {sections.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {sections.map((section) => {
                  const hasContent = section.output && section.output.trim();
                  return (
                    <Chip
                      key={section._id}
                      label={section.title}
                      variant={hasContent ? 'filled' : 'outlined'}
                      color={hasContent ? 'primary' : 'default'}
                      size="small"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: hasContent ? '#1976d2' : '#e0e0e0',
                          transform: 'translateY(-1px)',
                        },
                      }}
                      onClick={() => {
                        const element = sectionRefs.current[section._id];
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                    />
                  );
                })}
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {sections.filter((s) => s.output && s.output.trim()).length} of {sections.length} sections have content
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: '#666' }}>
              No sections yet.
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
            Generated using this project&apos;s USDM study definition data alongside the SAP intake form.
          </Typography>
        </Paper>

        {usdmNotice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {usdmNotice}
          </Alert>
        )}

        {/* Toolbar */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
            <Button
              variant="contained"
              startIcon={bulkGenerateProgress ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <RefreshIcon />}
              disabled={!!bulkGenerateProgress || sections.every((s) => s.output && s.output.trim())}
              onClick={handleGenerateAllSections}
            >
              {bulkGenerateProgress
                ? `Generating ${bulkGenerateProgress.current} of ${bulkGenerateProgress.total}...`
                : 'Generate All Sections'}
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
              Add Section
            </Button>
            <Button variant="outlined" color="error" startIcon={<ResetIcon />} onClick={handleReset}>
              Reset All Sections
            </Button>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Tooltip title={`${mode === 'edit' ? 'Switch to Preview Mode' : 'Switch to Edit Mode'} (Ctrl+P)`}>
              <Button
                variant={mode === 'edit' ? 'contained' : 'outlined'}
                color={mode === 'edit' ? 'primary' : 'success'}
                startIcon={mode === 'edit' ? <VisibilityIcon /> : <EditIcon />}
                onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')}
              >
                {mode === 'edit' ? 'Preview Mode' : 'Edit Mode'}
              </Button>
            </Tooltip>

            <StyleComplianceCheck
              sections={sections}
              documentType="SAP"
              documentId={actualProjectId}
              onSectionAutofixed={handleStyleAutofixApplied}
              disabled={sections.length === 0}
            />

            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />

            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              endIcon={<ArrowDownIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setExportMenuAnchor(null); handleExport('word'); }}>
                <ListItemIcon><DocxFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Export to DOCX</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setExportMenuAnchor(null); handleExport('xml'); }}>
                <ListItemIcon><XmlFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Export to XML</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setExportMenuAnchor(null); handleExportSapPdfNative(); }}>
                <ListItemIcon><PdfFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Export to PDF (Native)</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Paper>

        {/* Title Page — front matter rendered as its own dedicated page in the DOCX/PDF
            export (before Document Control), not a generated SAP section. Structured
            fields, not free text; auto-fill pulls what's available from the project's
            USDM/study identity, everything else is filled in manually here. */}
        <Accordion
          expanded={titlePageExpanded}
          onChange={(_, expanded) => setTitlePageExpanded(expanded)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ArrowDownIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Title Page</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {titlePageLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {titlePageError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTitlePageError(null)}>
                    {titlePageError}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAutoFillTitlePageFromUsdm}
                    disabled={titlePageAutoFilling || titlePageSaving}
                    startIcon={titlePageAutoFilling ? <CircularProgress size={14} /> : undefined}
                  >
                    Auto-fill from USDM
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveTitlePage}
                    disabled={titlePageSaving || !titlePageData}
                    startIcon={titlePageSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    Save Title Page
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {[
                    ['versionNumber', 'Version Number'],
                    ['fullTitle', 'Full Title'],
                    ['trialPhase', 'Trial Phase'],
                    ['trialAcronym', 'Trial Acronym'],
                    ['shortTitle', 'Short Title'],
                    ['sponsorProtocolIdentifier', "Sponsor Protocol Identifier"],
                    ['sponsorProductCodes', "Sponsor's Investigational Product Code(s)"],
                    ['investigationalProductNames', 'Investigational Product Name(s)'],
                    ['nctNumber', 'NCT No.'],
                    ['eudraCtNumber', 'EudraCT No.'],
                  ].map(([field, label]) => (
                    <Grid item xs={12} sm={6} key={field}>
                      <TextField
                        fullWidth
                        size="small"
                        label={label}
                        value={titlePageData?.[field] || ''}
                        onChange={(e) => handleTitlePageFieldChange(field, e.target.value)}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Sponsor Name and Address"
                      value={titlePageData?.sponsorNameAndAddress || ''}
                      onChange={(e) => handleTitlePageFieldChange('sponsorNameAndAddress', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Sponsor Confidentiality Statement"
                      value={titlePageData?.confidentialityStatement || ''}
                      onChange={(e) => handleTitlePageFieldChange('confidentialityStatement', e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Approvers and Signatories
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#888', mb: 2 }}>
                  Printed Name is entered here. Signature and Date are completed through the
                  electronic signature workflow, not typed in.
                </Typography>
                <Stack spacing={2}>
                  {(titlePageData?.approvers || []).map((approver, index) => (
                    <TextField
                      key={approver.role || index}
                      fullWidth
                      size="small"
                      label={approver.role}
                      value={approver.printedName || ''}
                      onChange={(e) => handleApproverNameChange(index, e.target.value)}
                    />
                  ))}
                </Stack>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {bulkGenerateProgress && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
            Generating section {bulkGenerateProgress.current} of {bulkGenerateProgress.total}: {bulkGenerateProgress.title}
          </Alert>
        )}

        <Grid container spacing={3}>
            {sections.map((section) => (
              <Grid item xs={12} key={section._id}>
                <Box
                  ref={(el) => { sectionRefs.current[section._id] = el; }}
                  sx={{
                  border: '1.5px solid #90caf9',
                  borderRadius: 3,
                  background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
                  p: 3,
                  mb: 4,
                  boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
                  position: 'relative',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                    transform: 'translateY(-2px) scale(1.01)',
                  },
                }}>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 2, fontWeight: 700 }}>
                      Section: {section.title}
                    </Typography>
                    {openQuestionsBySection[section._id]?.length > 0 && (
                      <Tooltip title="This section has open questions that need your input — click to answer and regenerate">
                        <Chip
                          icon={<OpenQuestionIcon />}
                          label={`${openQuestionsBySection[section._id].length} Open Question${openQuestionsBySection[section._id].length > 1 ? 's' : ''}`}
                          color="warning"
                          size="small"
                          onClick={() => {
                            setOpenQuestionAnswers({});
                            setOpenQuestionsDialogSectionId(section._id);
                          }}
                          sx={{ ml: 2, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Copy Section">
                      <IconButton size="small"><CopyIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Version History">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewVersionHistory(section._id)}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More Options">
                      <IconButton size="small" onClick={(event) => handleMenuOpen(event, section._id)}>
                        <MoreIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Tabs
                    value={sectionTabs[section._id] || 0}
                    onChange={(_, v) => {
                      setSectionTabs(tabs => ({ ...tabs, [section._id]: v }));
                      if (v === 1) fetchSectionVersions(section._id);
                    }}
                    sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(25, 118, 210, 0.10)', background: 'linear-gradient(90deg, #e3f2fd 60%, #fff 100%)', p: 1 }}
                  >
                    <Tab label="Edit" />
                    <Tab label="Version History" />
                  </Tabs>
                  { (sectionTabs[section._id] || 0) === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                        {mode === 'edit' ? 'Generated Content' : 'Document Preview'}
                      </Typography>
                      {mode === 'edit' ? (
                        <EditableSectionContent
                          html={editedTextContent[section._id] ?? unboldCrossReferenceSentences(section.output ?? '')}
                          resetKey={`${section._id}:${contentSyncNonce[section._id] || 0}`}
                          placeholder="Edit content here..."
                          onChange={(newHtml) => handleTextContentChange(section._id, newHtml)}
                        />
                      ) : (
                        <>
                          <Box sx={{ 
                            minHeight: 200, 
                            bgcolor: '#ffffff', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 2, 
                            p: 3, 
                            fontFamily: 'Times New Roman, serif',
                            fontSize: 14,
                            color: '#333333',
                            lineHeight: 1.6,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {section.output && section.output.trim() ? (
                              <Box dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(unboldCrossReferenceSentences(section.output)) }} />
                            ) : (
                              <Box sx={{ 
                                color: '#999',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: 4
                              }}>
                                [No content available for this section]
                              </Box>
                            )}
                          </Box>
                          {section.title === '2. Study Context' && trialSchemaImageUrl && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 2, 
                              bgcolor: 'grey.50', 
                              borderRadius: 2, 
                              border: '1px dashed #90caf9' 
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                2.4 Schema
                              </Typography>
                              <Box sx={{ textAlign: 'center' }}>
                                {trialSchemaImageUrl.trim().startsWith('<svg') ? (
                                  <img
                                    src={`data:image/svg+xml;utf8,${encodeURIComponent(trialSchemaImageUrl)}`}
                                    alt="Trial Schema"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 260,
                                      border: '1px solid #ccc',
                                      borderRadius: 8,
                                      background: '#fff'
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={trialSchemaImageUrl}
                                    alt="Trial Schema"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 260,
                                      border: '1px solid #ccc',
                                      borderRadius: 8,
                                      background: '#fff'
                                    }}
                                  />
                                )}
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                  (Auto-fetched from current protocol trial schema)
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Prompt</Typography>
                      {mode === 'edit' ? (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: 'column' }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            // Never falls back to section.prompt — only ever shows what's
                            // typed in the current session, never a previously-saved
                            // prompt. onBlur persists the draft (so it isn't lost if the
                            // user clicks elsewhere without generating), but it does NOT
                            // clear the display buffer — that's fine, because
                            // handleGenerateContent unconditionally clears the buffer once
                            // a generation completes, regardless of whether onBlur already
                            // fired first (clicking Generate/Regenerate blurs this field
                            // before its own onClick runs). So the box still reliably goes
                            // blank after every generation, even with onBlur in the mix.
                            value={editedPromptContent[section._id] || ''}
                            onChange={(e) => setEditedPromptContent(prev => ({ ...prev, [section._id]: e.target.value }))}
                            onBlur={() => {
                              const draft = editedPromptContent[section._id];
                              if (draft !== undefined && draft !== (section.prompt || '')) {
                                handleInputChange(section._id, 'prompt', draft);
                              }
                            }}
                            variant="outlined"
                            size="small"
                            placeholder="Optional: Enter a new custom prompt for this section. A previously used prompt (if any) is remembered and reused automatically even though it isn't shown here — leave blank to keep using it, or type a new one to replace it."
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="contained"
                              startIcon={section.output ? <RefreshIcon /> : undefined}
                              size="small"
                              disabled={isGenerating[section._id] || section.final}
                              onClick={() => handleGenerateContent(section._id)}
                              sx={{ flex: 1, minWidth: 150 }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : section.output ? 'Regenerate' : 'Generate Content'}
                            </Button>
                            <Tooltip title={section.final ? 'Unlock to allow Generate/Regenerate again' : 'Lock this section and protect it from Generate/Regenerate'}>
                              <Button
                                variant={section.final ? "outlined" : "contained"}
                                color={section.final ? "success" : "primary"}
                                size="small"
                                onClick={() => handleToggleFinal(section._id)}
                                sx={{ flex: 1, minWidth: 150 }}
                              >
                                {section.final ? 'Unlock Section' : 'Mark as Final'}
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{
                          minHeight: 100,
                          bgcolor: 'grey.50',
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          p: 2,
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          fontSize: 15,
                          color: 'text.secondary',
                        }}>
                          {section.prompt
                            ? <span style={{ color: '#aaa', fontStyle: 'italic' }}>A custom prompt is saved for this section (not shown here).</span>
                            : <span style={{ color: '#aaa' }}>No custom prompt - will use default prompt when generating content</span>}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  )}
                  { (sectionTabs[section._id] || 0) === 1 && (
                    <Box>
                      {sectionVersions[section._id]?.length > 0 ? (
                        sectionVersions[section._id].map((version, idx) => (
                          <Paper key={version._id || idx} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Version {version.versionNumber}</Typography>
                            <Typography variant="caption" color="text.secondary">{version.createdAt ? new Date(version.createdAt).toLocaleString() : ''}</Typography>
                            {version.prompt && (
                              <Typography variant="body2" sx={{ mt: 1 }}><strong>Prompt:</strong> {version.prompt}</Typography>
                            )}
                            {version.output && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Content:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 1,
                                    fontSize: 14,
                                    lineHeight: 1.5,
                                    bgcolor: '#fafafa',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(version.output) }}
                                />
                              </Box>
                            )}
                            <Button size="small" sx={{ mt: 1 }} onClick={async () => {
                              await handleRevertVersion(section._id, version);
                              fetchSectionVersions(section._id);
                            }}>Revert to this Version</Button>
                </Paper>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No version history available for this section.</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleViewVersionHistory(selectedSectionId)}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Version History</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDeleteSection(selectedSectionId)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Section</ListItemText>
          </MenuItem>
        </Menu>

        {/* Open Questions dialog — answer flagged gaps, then regenerate the section with those answers folded in */}
        <Dialog
          open={Boolean(openQuestionsDialogSectionId)}
          onClose={handleCloseOpenQuestionsDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Open Questions</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This section flagged the following as open questions instead of guessing. Answer what you can — the section will be regenerated using your answers, and anything left blank will stay flagged as open.
            </Typography>
            {(openQuestionsBySection[openQuestionsDialogSectionId] || []).map((question, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{idx + 1}. {question}</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  placeholder="Your answer (leave blank to leave this open)"
                  value={openQuestionAnswers[idx] || ''}
                  onChange={(e) => setOpenQuestionAnswers((prev) => ({ ...prev, [idx]: e.target.value }))}
                />
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOpenQuestionsDialog} disabled={isResolvingOpenQuestions}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitOpenQuestionAnswers}
              disabled={isResolvingOpenQuestions}
              startIcon={isResolvingOpenQuestions ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
            >
              {isResolvingOpenQuestions ? 'Regenerating...' : 'Submit & Regenerate'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={xmlPushSnackbar.open}
          autoHideDuration={6000}
          onClose={closeXmlPushSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeXmlPushSnackbar} severity={xmlPushSnackbar.severity} sx={{ width: '100%' }}>
            {xmlPushSnackbar.message}
          </Alert>
        </Snackbar>

        {/* Scroll to Top Button */}
        <ScrollToTop />
      </Box>
    </ThemeProvider>
  );
};

export default SAPEditorUsdm; 